import _ from 'lodash'
import { isFlatVisualBuilderSku } from '../../utils/visualBuilderSku'

export const UNKNOWN_APPLICATION_ID = '__layout_builder_unknown__'
export const UNKNOWN_APPLICATION = {
  id: UNKNOWN_APPLICATION_ID,
  name: 'Unknown — describe in notes',
  description: 'Use when the application is not in the catalog. Notes remain optional.',
  fileTypes: []
}

export const PROTOCOLS = ['AS2', 'AWS S3', 'eAdaptor', 'Email', 'HTTPS API', 'SFTP']

export const findIntegrationType = (catalog, integrationTypeId) =>
  catalog.integrations.find(item => item.id === integrationTypeId)

export const findFlowType = (integrationType, flowTypeId) =>
  integrationType?.flows?.find(item => item.id === flowTypeId)

// Visual Builder flows have no user-chosen flow type: every one is the integration type's single
// catalog flow type (`vb.vb`), which carries the license's max_flows.
export const visualBuilderFlowType = integrationType =>
  isFlatVisualBuilderSku(integrationType?.id) ? integrationType.flows?.[0] : undefined

// Groups the file types behind a set of adapter ids by the application they belong to, for
// display. Applications sorted case-insensitively by name; applications with none of the adapters,
// and adapter ids that map to no application, are skipped. Returns new objects.
export const applicationsForAdapters = (adapterIds, applications) => {
  const wanted = new Set(adapterIds || [])
  return _.sortBy(
    (applications || [])
      .map(application => ({
        id: application.id,
        name: application.name,
        fileTypes: _.uniqBy((application.fileTypes || []).filter(fileType => wanted.has(fileType.id)), 'id')
          .map(fileType => ({ id: fileType.id, name: fileType.name }))
      }))
      .filter(application => application.fileTypes.length > 0),
    application => (application.name || '').toLowerCase()
  )
}

export const eligibleApplications = (applications, allowedAdapterIds, savedSide = {}) => {
  const allowed = new Set(allowedAdapterIds || [])
  const eligible = applications
    .map(application => ({
      ...application,
      fileTypes: _.uniqBy(
        (application.fileTypes || []).filter(fileType => allowed.has(fileType.id)),
        'id'
      )
    }))
    .filter(application => application.fileTypes.length > 0)

  const savedApplication = eligible.find(application => application.id === savedSide.application_id)
  if (savedApplication && savedSide.file_type_id && !savedApplication.fileTypes.some(fileType => fileType.id === savedSide.file_type_id)) {
    savedApplication.fileTypes.push({
      id: savedSide.file_type_id,
      name: `${savedSide.file_type_label || 'Saved file type'} (unavailable)`,
      historical: true
    })
  } else if (savedSide.application_id && !savedApplication) {
    eligible.push({
      id: savedSide.application_id,
      name: `${savedSide.application_label || 'Saved application'} (unavailable)`,
      description: 'This saved choice is no longer available in the current catalog.',
      historical: true,
      fileTypes: savedSide.file_type_id
        ? [{ id: savedSide.file_type_id, name: `${savedSide.file_type_label || 'Saved file type'} (unavailable)`, historical: true }]
        : []
    })
  }

  return [...eligible, UNKNOWN_APPLICATION]
}

export const sideIsCompatible = (side, flowType, direction, applications = []) => {
  if (side?.unknown) return true
  const allowed = direction === 'source' ? flowType?.sourceAdapters : flowType?.destinationAdapters
  if (side.file_type_id) return (allowed || []).includes(side.file_type_id)
  if (!side?.application_id) return true
  const application = applications.find(item => item.id === side.application_id)
  if (!application) return true
  return (application.fileTypes || []).some(fileType => (allowed || []).includes(fileType.id))
}

const catalogFlows = catalog => (catalog?.integrations || []).flatMap(integration => integration.flows || [])

// Visual Builder flows are not tied to one flow type: their side options are the union across
// the whole catalog. Sources: every source adapter any catalog flow accepts.
export const visualBuilderSourceAdapters = catalog =>
  _.uniq(catalogFlows(catalog).flatMap(flow => flow.sourceAdapters || []))

// Destinations: with no source chosen, every destination adapter; otherwise those of every
// catalog flow whose source adapters include the chosen one.
export const visualBuilderDestinationAdapters = (catalog, sourceAdapterId) =>
  _.uniq(catalogFlows(catalog)
    .filter(flow => !sourceAdapterId || (flow.sourceAdapters || []).includes(sourceAdapterId))
    .flatMap(flow => flow.destinationAdapters || []))

export const summarizeSide = side => {
  if (side?.unknown) return 'Unknown — see notes'
  if (!side?.application_label) return 'Not specified'
  return [side.application_label, side.file_type_label, side.protocol].filter(Boolean).join(' · ')
}
